import { ReactNode, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import sirppiLogo from "@/assets/sirppi-logo.svg";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { UserProfileMenu } from "@/components/UserProfileMenu";
import { BottomNavigation } from "./BottomNavigation";
import { MobileHeader } from "./MobileHeader";
import { DesktopNav } from "./desktop/DesktopNav";
import { TabletSidebar } from "./navigation/TabletSidebar";
import { TabletSidebarProvider, useTabletSidebar } from "@/contexts/TabletSidebarContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsTablet } from "@/hooks/use-tablet";
import { useTokenValidation } from "@/hooks/useTokenValidation";
import { cn } from "@/lib/utils";
import { UnifiedFooter } from "@/components/shared/UnifiedFooter";

// Helper function to determine branding text based on route
const getBrandingText = (pathname: string): string => {
  // Auth route uses Sirppi branding
  if (pathname === '/login') {
    return 'Sirppi.ai';
  }
  
  // Profile and Settings use Sirppi branding (platform-level pages)
  if (pathname === '/profile' || pathname === '/settings' || 
      pathname.startsWith('/profile/') || pathname.startsWith('/settings/')) {
    return 'Sirppi.ai';
  }
  
  // Marketing pages use Sirppi branding
  if (pathname === '/' || pathname === '/products' || pathname === '/about' || 
      pathname === '/contact' || pathname === '/pricing' ||
      pathname === '/terms' || pathname === '/privacy' || pathname === '/refund' || 
      pathname === '/cancellation') {
    return 'Sirppi.ai';
  }
  
  // Studio pages
  if (pathname.startsWith('/studio')) {
    return 'Career Studio';
  }
  
  // Default to Career Path for app pages
  return 'Career Path';
};

// Helper to determine footer variant based on route
const getFooterVariant = (pathname: string): 'sirppi' | 'career-path' => {
  if (pathname === '/login') {
    return 'sirppi';
  }
  if (pathname === '/' || pathname === '/products' || pathname === '/about' || 
      pathname === '/contact' || pathname === '/pricing' ||
      pathname === '/terms' || pathname === '/privacy' || pathname === '/refund' || 
      pathname === '/cancellation') {
    return 'sirppi';
  }
  return 'career-path';
};

interface LayoutProps {
  children: ReactNode;
  showBackButton?: boolean;
  showHomeButton?: boolean;
  title?: string;
}

const LayoutContent = ({ 
  children, 
  showBackButton = false, 
  showHomeButton = false,
  title 
}: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { sidebarWidth } = useTabletSidebar();
  const { result } = useTokenValidation();
  const isAdminView = location.pathname.startsWith('/admin');
  
  // Get dynamic branding based on current route
  const brandingText = useMemo(() => getBrandingText(location.pathname), [location.pathname]);
  const footerVariant = useMemo(() => getFooterVariant(location.pathname), [location.pathname]);

  // Mobile layout
  if (isMobile) {
    return (
      <div className="fixed inset-0 flex flex-col bg-background">
        <MobileHeader 
          title={title || brandingText} 
          showBack={showBackButton}
          showShare={false}
        />
        
        <main className="flex-1 overflow-y-auto overscroll-contain pb-[calc(4rem+env(safe-area-inset-bottom))]">
          {children}
        </main>

        <BottomNavigation />
      </div>
    );
  }

  // Desktop/Tablet layout
  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content - Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Toast notifications aria-live region */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" id="toast-announcer" />

      {/* Tablet Sidebar Navigation - only shows on tablet screens */}
      <TabletSidebar />

      {/* Header Navigation - Clean professional style */}
      <header 
        className="sticky top-0 z-50 border-b border-border/30 bg-white/80 backdrop-blur-lg supports-[backdrop-filter]:bg-white/60 transition-all duration-300"
        style={{ marginLeft: isTablet ? sidebarWidth : 0 }}
      >
        <div className="container-apple">
          {isAdminView ? (
            // Admin Navigation: Simple 2-column layout (branding | profile)
            <div className="flex items-center justify-between py-4">
              {/* Left: Sirppi Branding */}
              <Link 
                to="/" 
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <img src={sirppiLogo} alt="Sirppi" className="h-8 w-auto object-contain" />
                <span className="text-base font-semibold text-gradient-apple">{brandingText}</span>
              </Link>
              
              {/* Right: Profile Menu - contains role switcher for admin access */}
              <UserProfileMenu />
            </div>
          ) : (
            // Regular Navigation: 3-column layout (branding | navigation | profile)
            // Hide desktop nav on tablet since we have sidebar
            <div className="flex items-center justify-between py-4">
              {/* Left: Career Path Branding with separator + optional back button */}
              <div className="flex items-center gap-4">
                {showBackButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    className="hover:bg-muted/50 rounded-2xl"
                    aria-label="Go back"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}
                <Link 
                  to="/" 
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <img src={sirppiLogo} alt="Sirppi" className="h-8 w-auto object-contain" />
                  <span className="text-base font-semibold text-gradient-apple">{brandingText}</span>
                </Link>
                {!isTablet && !showBackButton && <div className="h-6 w-px bg-border/50" />}
              </div>
              
              {/* Center: Navigation buttons - hide on tablet (using sidebar) */}
              {!isTablet && (
                <div className="flex-1 flex justify-center">
                  <DesktopNav />
                </div>
              )}
              
              {/* Right: Separator + Profile Menu */}
              <div className="flex items-center gap-4">
                {!isTablet && <div className="h-6 w-px bg-border/50" />}
                <UserProfileMenu />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - offset for tablet sidebar */}
      <main 
        id="main-content" 
        className="flex-1 transition-all duration-300"
        style={{ marginLeft: isTablet ? sidebarWidth : 0 }}
      >
        {children}
      </main>

      {/* Footer - uses dynamic variant based on route */}
      <div style={{ marginLeft: isTablet ? sidebarWidth : 0 }}>
        <UnifiedFooter variant={footerVariant} />
      </div>
    </div>
  );
};

export const Layout = (props: LayoutProps) => {
  return (
    <TabletSidebarProvider>
      <LayoutContent {...props} />
    </TabletSidebarProvider>
  );
};
